import { InitiativeStatus } from 'legacy/lut/classes/models/partner360/IFormThingsToImprove'
import { P360Card } from 'legacy/lut/components/widgets/public/partner360/p360Card'
import { selectJBPSettings } from 'legacy/lut/features/partner360/jbpSettings'
import { FC, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { Flex } from 'core-components/library/flex'
import { KEY_INITIATIVE_STATUSES } from 'partner360/configs/keyInitiatives/keyInitiativeStatusesConfig'
import { TOUR_TARGETS } from 'partner360/configs/widgetTour/planningActivitiesTourTargets'
import { TKeyInitiative } from 'partner360/entities/keyInitiatives'
import { getKeyInitiativeStatusTranslationKey } from 'partner360/entities/keyInitiatives/model/status'
import { TRANSLATION_KEYS } from 'partner360/features/keyInitiatives/configs/translations'
import {
  DashboardChartMeasure,
  DashboardGroupBy,
} from 'partner360/features/keyInitiatives/model/types'
import {
  getGoalField,
  getInitiativeIncrementalField,
} from 'partner360/features/keyInitiatives/ui/helpers/fieldHelper'
import { getBusinessPriorityNames } from 'partner360/features/keyInitiatives/ui/helpers/getBusinessPriorityNames'
import { getNumberValue } from 'partner360/features/keyInitiatives/ui/helpers/getNumberValue'
import { getGroupValue } from 'partner360/features/keyInitiatives/ui/helpers/groupHelper'
import { useKeyInitiativesControlsForm } from 'partner360/features/keyInitiatives/ui/hooks/useKeyInitiativesControlsForm'
import { KeyInitiativesBarChart } from 'partner360/features/keyInitiatives/ui/keyInitiativesBarChart'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import { DashboardLabel } from '../../common/dashboardLabel/DashboardLabel'
import { INITIATIVES_CHART_LABEL_MAX_LENGTH, QUARTERS } from '../configs'
import { formatChartValue } from '../helpers/formatChartValue'

import styles from './InitiativesBarChart.module.scss'

interface IInitiativesBarChartProps {
  data: TKeyInitiative[]
  activeCurrency: string
  actualsType: ActualsValueTypes
}

const getMeasureValue = (
  item: TKeyInitiative,
  chartMeasure: DashboardChartMeasure,
  actualsType: ActualsValueTypes,
  activeCurrency: string
) => {
  if (chartMeasure === DashboardChartMeasure.Count) {
    return 1
  }

  const field =
    chartMeasure === DashboardChartMeasure.Goal
      ? getGoalField(actualsType, activeCurrency)
      : getInitiativeIncrementalField(actualsType, activeCurrency)

  return getNumberValue(item, field)
}

const splitGroupValue = (value?: string | null) =>
  value
    ? value
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean)
    : ['N/A']

const statusOrder = new Map(KEY_INITIATIVE_STATUSES.map(({ key }, index) => [key, index]))

const sortByStatusOrder = (a: string, b: string) =>
  (statusOrder.get(a as InitiativeStatus) ?? Number.MAX_SAFE_INTEGER) -
  (statusOrder.get(b as InitiativeStatus) ?? Number.MAX_SAFE_INTEGER)

export const InitiativesBarChart: FC<IInitiativesBarChartProps> = ({
  data,
  activeCurrency,
  actualsType,
}) => {
  const { groupBy, chartMeasure } = useKeyInitiativesControlsForm()
  const { kiBusinessPriorities } = useSelector(selectJBPSettings)

  const formatValue = useMemo(() => {
    const isFinancialMeasure = chartMeasure !== DashboardChartMeasure.Count
    return formatChartValue(activeCurrency, isFinancialMeasure)
  }, [chartMeasure, activeCurrency])

  const chartData = useMemo(() => {
    const map = new Map<string, number>()

    data.forEach((item) => {
      const rawGroupValue = getGroupValue(item, groupBy)

      const groupValues =
        groupBy === DashboardGroupBy.BusinessPriority
          ? getBusinessPriorityNames(kiBusinessPriorities, rawGroupValue)
          : splitGroupValue(rawGroupValue)

      const normalizedGroupValues = groupValues.length ? groupValues : ['N/A']
      const value = getMeasureValue(item, chartMeasure, actualsType, activeCurrency)

      normalizedGroupValues.forEach((groupValue) => {
        const currentValue = map.get(groupValue) || 0

        const valueForGroup =
          groupBy === DashboardGroupBy.Period && chartMeasure !== DashboardChartMeasure.Count
            ? value / normalizedGroupValues.length
            : value

        map.set(groupValue, currentValue + valueForGroup)
      })
    })

    if (groupBy === DashboardGroupBy.Period) {
      return QUARTERS.map((period) => ({
        name: period,
        value: map.get(period) || 0,
      }))
    }

    const entries = Array.from(map.entries())

    if (groupBy === DashboardGroupBy.Status) {
      entries.sort(([statusA], [statusB]) => sortByStatusOrder(statusA, statusB))
    } else {
      entries.sort(([nameA], [nameB]) => {
        if (nameA === 'N/A') {
          return 1
        }
        if (nameB === 'N/A') {
          return -1
        }

        return 0
      })
    }

    return entries.map(([name, value]) => ({
      name: groupBy === DashboardGroupBy.Status ? getKeyInitiativeStatusTranslationKey(name) : name,
      value,
    }))
  }, [data, groupBy, kiBusinessPriorities, chartMeasure, actualsType, activeCurrency])

  return (
    <P360Card className={styles.root}>
      <div data-tour-id={TOUR_TARGETS.analysisBreakdownChart}>
        <Flex justifyContent={'space-between'} alignItems={'center'}>
          <DashboardLabel
            translationKey={TRANSLATION_KEYS.dashboard.charts.initiativesBar.title}
            type={'title'}
            replaceParams={[
              { key: '{chartMeasure}', value: chartMeasure },
              { key: '{groupBy}', value: groupBy },
              { key: '{activeCurrency}', value: activeCurrency },
              { key: '{actualsType}', value: actualsType },
            ]}
          />
          <DashboardLabel
            translationKey={TRANSLATION_KEYS.dashboard.charts.initiativesBar.subTitle}
            type={'subTitle'}
            replaceParams={[
              { key: '{chartMeasure}', value: chartMeasure },
              { key: '{activeCurrency}', value: activeCurrency },
              { key: '{actualsType}', value: actualsType },
            ]}
          />
        </Flex>
        <KeyInitiativesBarChart
          data={chartData}
          formatValue={formatValue}
          maxLabelLength={INITIATIVES_CHART_LABEL_MAX_LENGTH}
        />
      </div>
    </P360Card>
  )
}
